// SHADOW MARKET™ — Caso de Estudio detail page inside the RE₿EL Lab de Mercado.
// Route: /academy/lab-mercado/:slug
import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  FlaskConical,
  Clock3,
  GraduationCap,
  ListChecks,
  Layers,
  Lightbulb,
  Target,
  Activity,
  History,
  CheckCircle2,
} from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { SimulatorRankCard } from "../components/SimulatorRankCard";
import { useLanguage } from "../i18n/LanguageContext";
import {
  CASOS_ESTUDIO,
  CASO_INDEX_BY_SLUG,
  getCaso,
  getCasosUi,
} from "../data/casosEstudio";

const tipoDot = {
  critico: "bg-red-500 border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.7)]",
  alcista: "bg-emerald-400 border-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]",
  bajista: "bg-orange-500 border-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.6)]",
  neutro: "bg-white/40 border-white/40",
};
const tipoBadge = {
  critico: "border-red-400/40 bg-red-400/10 text-red-300",
  alcista: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  bajista: "border-orange-400/40 bg-orange-400/10 text-orange-300",
  neutro: "border-white/20 bg-white/[0.06] text-white/55",
};
const nivelCfg = {
  basico: "border-white/15 bg-white/[0.05] text-white/60",
  intermedio: "border-orange-400/40 bg-orange-400/10 text-orange-300",
  avanzado: "border-red-400/40 bg-red-400/10 text-red-300",
};

const CasoEstudioPage = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const ui = getCasosUi(lang);

  const idx = CASO_INDEX_BY_SLUG[slug];
  const caso = useMemo(() => getCaso(slug, lang), [slug, lang]);

  if (!caso || idx === undefined)
    return <Navigate to="/academy" replace />;

  const prev = idx > 0 ? CASOS_ESTUDIO[idx - 1] : null;
  const next = idx < CASOS_ESTUDIO.length - 1 ? CASOS_ESTUDIO[idx + 1] : null;
  const tPrev = prev ? getCaso(prev.slug, lang) : null;
  const tNext = next ? getCaso(next.slug, lang) : null;

  const carouselLabels = caso.datosCarouselLabels || {};

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      {/* Ambient background — match Academy / Patron detail page */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,140,0,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(168,85,247,0.10),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_70%,rgba(0,229,255,0.10),transparent_55%)]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      {/* HEADER */}
      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/academy"
            state={{ view: "lab" }}
            className="group inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="caso-detail-back"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">
              {ui.back_lab}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          </div>
          <div className="flex items-center gap-3">
            <SimulatorRankCard />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 py-8 md:px-10">
        {/* Breadcrumb */}
        <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
          <Link to="/academy" className="transition-colors hover:text-orange-300">
            {ui.breadcrumb_academy}
          </Link>
          <span className="px-2 text-white/25">→</span>
          <Link
            to="/academy"
            state={{ view: "lab" }}
            className="transition-colors hover:text-orange-300"
          >
            {ui.breadcrumb_lab}
          </Link>
          <span className="px-2 text-white/25">→</span>
          <span className="text-orange-300">{caso.titulo}</span>
        </p>

        {/* Eyebrow + title */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mb-5"
        >
          <div
            className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 backdrop-blur"
            style={{
              borderColor: `${caso.colorCategoria}55`,
              background: `${caso.colorCategoria}14`,
              color: caso.colorCategoria,
            }}
          >
            <FlaskConical className="h-3.5 w-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em]">
              {caso.categoria}
            </span>
          </div>
          <h1
            className="font-display text-3xl font-black tracking-tight text-white md:text-5xl"
            data-testid="caso-detail-title"
          >
            {caso.titulo}
          </h1>
          <p className="mt-3 max-w-3xl text-sm text-white/65 md:text-base">
            {caso.subtitulo}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/65">
              <Clock3 className="h-3 w-3" />
              {caso.duracionLectura}
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${nivelCfg[caso.nivel] || nivelCfg.intermedio}`}
            >
              {ui.nivel_label[caso.nivel] || caso.nivel}
            </span>
          </div>
        </motion.div>

        {/* Key data grid */}
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mb-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
          data-testid="caso-detail-data-grid"
        >
          {(caso.datosCarousel || []).map((dato, i) => (
            <div
              key={i}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-3 backdrop-blur-xl"
              data-testid={`caso-data-${i}`}
            >
              <div
                className="font-mono text-base font-black leading-tight md:text-xl"
                style={{ color: dato.color }}
              >
                {dato.valor}
              </div>
              <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.16em] text-white/45">
                {carouselLabels[dato.key] || dato.key}
              </div>
            </div>
          ))}
        </motion.section>

        {/* Resumen */}
        <Section icon={Layers} color="text-orange-300" title={ui.section_resumen} testid="caso-resumen">
          <p className="text-sm leading-relaxed text-white/75 md:text-base">{caso.resumen}</p>
        </Section>

        {/* Contexto */}
        <Section icon={History} color="text-amber-300" title={ui.section_contexto} testid="caso-contexto">
          <p className="text-sm leading-relaxed text-white/75 md:text-base">{caso.contexto}</p>
        </Section>

        {/* Timeline */}
        <Section icon={Activity} color="text-cyan-300" title={ui.section_timeline} testid="caso-timeline">
          <div className="relative">
            <span className="absolute bottom-2 left-[7px] top-2 w-px bg-gradient-to-b from-white/30 via-white/10 to-transparent" />
            <ol className="space-y-5">
              {(caso.timeline || []).map((evento, i) => (
                <li key={i} className="relative pl-7" data-testid={`caso-timeline-${i}`}>
                  <span
                    className={`absolute left-0 top-1.5 inline-block h-[14px] w-[14px] rounded-full border-2 ${tipoDot[evento.tipo] || tipoDot.neutro}`}
                  />
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                      {evento.fecha}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.18em] ${tipoBadge[evento.tipo] || tipoBadge.neutro}`}
                    >
                      {ui.tipo_label[evento.tipo] || evento.tipo}
                    </span>
                  </div>
                  <h3 className="mt-1 text-sm font-bold text-white md:text-base">
                    {evento.titulo}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-white/60 md:text-sm">
                    {evento.descripcion}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </Section>

        {/* Análisis Técnico */}
        <Section icon={Target} color="text-cyan-300" title={ui.section_tecnico} testid="caso-tecnico">
          <p className="text-sm leading-relaxed text-white/75 md:text-base">{caso.analisisTecnico}</p>
        </Section>

        {/* On-chain (optional) */}
        {caso.senalesOnchain ? (
          <Section icon={Activity} color="text-purple-300" title={ui.section_onchain} testid="caso-onchain">
            <p className="text-sm leading-relaxed text-white/75 md:text-base">{caso.senalesOnchain}</p>
          </Section>
        ) : null}

        {/* Lecciones */}
        <Section icon={ListChecks} color="text-emerald-300" title={ui.section_lecciones} testid="caso-lecciones">
          <ol className="space-y-3">
            {(caso.leccionesAprendidas || []).map((l, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`caso-leccion-${i}`}>
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-emerald-400/40 bg-emerald-400/10 text-[10px] font-black text-emerald-300">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm leading-relaxed text-white/80">{l}</span>
              </li>
            ))}
          </ol>
        </Section>

        {/* Cómo aplicarlo */}
        <section
          className="mb-8 overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.08] to-transparent p-6 backdrop-blur-xl"
          data-testid="caso-aplicar"
        >
          <h2 className="mb-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            <Lightbulb className="h-3.5 w-3.5" />
            {ui.section_aplicar}
          </h2>
          <p className="text-sm leading-relaxed text-white/85 md:text-base">{caso.comoAplicarlo}</p>
        </section>

        {/* Conclusión */}
        <section
          className="mb-12 overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-400/[0.08] to-transparent p-6 backdrop-blur-xl"
          data-testid="caso-conclusion"
        >
          <h2 className="mb-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {ui.section_conclusion}
          </h2>
          <p className="text-sm italic leading-relaxed text-white/85 md:text-base">{caso.conclusionFinal}</p>
        </section>

        {/* Nav */}
        <nav className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {prev && tPrev ? (
            <Link
              to={`/academy/lab-mercado/${prev.slug}`}
              className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left backdrop-blur-xl transition-all hover:border-orange-400/40 hover:text-white"
              data-testid="caso-detail-prev"
            >
              <ArrowLeft className="h-4 w-4 shrink-0 text-orange-300 transition-transform group-hover:-translate-x-1" />
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/40">
                  {ui.nav_prev}
                </div>
                <div className="truncate text-xs font-bold text-white">
                  {tPrev.titulo}
                </div>
              </div>
            </Link>
          ) : (
            <div />
          )}

          <Link
            to="/academy"
            state={{ view: "lab" }}
            className="group inline-flex items-center justify-center gap-2 rounded-xl border border-orange-400/40 bg-orange-400/[0.08] px-4 py-3 text-center text-[10px] font-black uppercase tracking-[0.22em] text-orange-300 backdrop-blur-xl transition-all hover:bg-orange-400/[0.14]"
            data-testid="caso-detail-all"
          >
            <GraduationCap className="h-4 w-4" />
            {ui.nav_all}
          </Link>

          {next && tNext ? (
            <Link
              to={`/academy/lab-mercado/${next.slug}`}
              className="group flex items-center justify-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-right backdrop-blur-xl transition-all hover:border-orange-400/40 hover:text-white"
              data-testid="caso-detail-next"
            >
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/40">
                  {ui.nav_next}
                </div>
                <div className="truncate text-xs font-bold text-white">
                  {tNext.titulo}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-orange-300 transition-transform group-hover:translate-x-1" />
            </Link>
          ) : (
            <div />
          )}
        </nav>
      </main>
    </div>
  );
};

// Reusable glass-card section with heading.
const Section = ({ icon: Icon, color, title, testid, children }) => (
  <section
    className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl"
    data-testid={testid}
  >
    <h2
      className={`mb-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] ${color}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {title}
    </h2>
    {children}
  </section>
);

export default CasoEstudioPage;
